import type { GatewayBrowserClient } from "../gateway.ts";
import type { LogEntry, LogLevel } from "../types.ts";
// JAISIU-types-sweep: this module used to import `parseLogLine` from the
// BACKEND (`src/logging/parse-log-line.js`), whose contract is
// `ParsedLogLine | null` — neither half fits `LogEntry[]` (`null` in,
// un-normalised `level: string` out). Worse, the `null` was never
// filtered, so a non-JSON tail line landed in `state.entries` as a
// literal `null`. `controllers/logs.ts` is the UI counterpart: same
// JSON / scope parsing, but it returns a real `LogEntry` and falls back
// to `{ raw, message }` for a line that is not JSON — which is exactly
// what a log tail can contain.
import { parseLogLine } from "./logs.ts";

const LOG_BUFFER_LIMIT = 2000;

export type SessionLogStatePrefix = "openclawLogs" | "jaisiuLogs" | "gatewayLogs";

export function stateAdapter(
  app: { [key: string]: unknown },
  prefix: SessionLogStatePrefix,
): SessionLogsState {
  const p = prefix;
  return {
    get loading() {
      return app[`${p}Loading`] as boolean;
    },
    set loading(v: boolean) {
      app[`${p}Loading`] = v;
    },
    get error() {
      return app[`${p}Error`] as string | null;
    },
    set error(v: string | null) {
      app[`${p}Error`] = v;
    },
    get fileCount() {
      return app[`${p}FileCount`] as number | null;
    },
    set fileCount(v: number | null) {
      app[`${p}FileCount`] = v;
    },
    get entries() {
      return app[`${p}Entries`] as LogEntry[];
    },
    set entries(v: LogEntry[]) {
      app[`${p}Entries`] = v;
    },
    get cursor() {
      return app[`${p}Cursor`] as number | null;
    },
    set cursor(v: number | null) {
      app[`${p}Cursor`] = v;
    },
    get truncated() {
      return app[`${p}Truncated`] as boolean;
    },
    set truncated(v: boolean) {
      app[`${p}Truncated`] = v;
    },
  };
}

export async function loadSessionLogsByPrefix(
  app: { client?: GatewayBrowserClient | null } & Record<string, unknown>,
  prefix: SessionLogStatePrefix,
  opts?: { reset?: boolean; quiet?: boolean },
) {
  if (!app.client) return;
  const state = stateAdapter(app, prefix);
  const method =
    `logs.tail.${prefix === "openclawLogs" ? "openclaw" : prefix === "jaisiuLogs" ? "jaisiu" : "gateway"}` as const;
  return loadTailSessionLogs(app.client, state, method, opts);
}

export type SessionLogsState = {
  loading: boolean;
  error: string | null;
  fileCount: number | null;
  entries: LogEntry[];
  cursor: number | null;
  truncated: boolean;
};

export async function loadTailSessionLogs(
  client: GatewayBrowserClient,
  state: SessionLogsState,
  method: "logs.tail.openclaw" | "logs.tail.jaisiu" | "logs.tail.gateway",
  opts?: { reset?: boolean; quiet?: boolean },
) {
  if (!client) return;
  if (state.loading && !opts?.quiet) return;
  if (!opts?.quiet) state.loading = true;
  state.error = null;

  try {
    const res = (await client.request(method, {
      cursor: opts?.reset ? 0 : (state.cursor ?? 0),
      limit: 500,
    })) as {
      lines?: unknown;
      fileCount?: number;
      truncated?: boolean;
      cursor?: number | null;
      reset?: boolean;
    };

    const lines = Array.isArray(res.lines)
      ? (res.lines.filter((l) => typeof l === "string") as string[])
      : [];
    const entries = lines.map(parseLogLine);

    const shouldReset = Boolean(opts?.reset || res.reset || state.cursor == null);
    state.entries = shouldReset ? entries : [...state.entries, ...entries].slice(-LOG_BUFFER_LIMIT);

    if (typeof res.cursor === "number") state.cursor = res.cursor;
    else if (res.cursor === null) state.cursor = null;

    state.fileCount = typeof res.fileCount === "number" ? res.fileCount : null;
    state.truncated = Boolean(res.truncated);
  } catch (err) {
    state.error = String(err);
  } finally {
    if (!opts?.quiet) state.loading = false;
  }
}

export function filterSessionLogEntries(
  entries: LogEntry[],
  filterText: string,
  levelFilters: Record<LogLevel, boolean>,
): LogEntry[] {
  return entries.filter((entry) => {
    if (filterText) {
      const needle = filterText.toLowerCase();
      const raw = entry.raw.toLowerCase();
      const msg = (entry.message ?? "").toLowerCase();
      if (!raw.includes(needle) && !msg.includes(needle)) return false;
    }
    if (entry.level && !levelFilters[entry.level]) return false;
    return true;
  });
}
